import { OptionTypeEnum, type RequiredProperties, type Security, formattedUTCDate } from '@talos/kyoko';
import {
  ALL_EXPIRIES_ITEM,
  ALL_OPTION_FIELDS,
  CME_AVAILABLE_OPTION_COLUMNS,
  CME_MARKET_NAME,
  type ColumnVisibilityMapping,
  type DEFAULT_OPTION_COLUMNS_IS_VISIBLE,
  FALCONX_AVAILABLE_OPTION_COLUMNS,
  FALCONX_MARKET_NAME,
  type OPTION_HEADER_MAP,
  STS_AVAILABLE_OPTION_COLUMNS,
  STS_DIGITAL_MARKET_NAME,
} from './types';

export function formatOptionExpiry(expiry: string, monthFormat: '{Month}' | '{Mon}' = '{Month}') {
  if (expiry === ALL_EXPIRIES_ITEM) {
    return expiry;
  }

  return formattedUTCDate(expiry, `{dd} ${monthFormat} {yyyy}`);
}

export function getOptionCellClassName(side: OptionTypeEnum) {
  return `${side}-cell`;
}

export function getOptionSecurityRowID(security: RequiredProperties<Security, 'Expiration' | 'StrikePrice'>) {
  const settlementParts = security.DerivativeSettlementType
    ? [security.DerivativeSettlementType, security.SettlementCurrency].filter(Boolean)
    : [];
  return [security.Expiration, security.StrikePrice, security.BaseCurrency, ...settlementParts].join('-');
}

export function isOptionTypeEnum(value: any): value is OptionTypeEnum {
  return value === OptionTypeEnum.Call || value === OptionTypeEnum.Put;
}

/**
 * Generates a column ID that is also the path for the field value.
 * Since option chain is rendering each column twice, we prepend the column
 * with the side they are rendering on.
 *
 * @param side The side of the option chain this column is rendered on.
 * @param field key on OptionChainDataRow.
 */
export function getOptionColumnId(side: OptionTypeEnum, field: keyof typeof OPTION_HEADER_MAP | undefined) {
  return `${side}.${field}`;
}

/**
 * Given the current state of column defs visibility and the current market name, will return the
 * columns we can keep visible, and the ones that need to be hidden.
 */
export function getColumnVisibilityArrays(
  /** The current state of column visibility coming from the filter slice */
  columnDefsVisibility: Record<string, boolean>,
  marketName: string | undefined
) {
  const getColumnVisibility = (field: keyof typeof DEFAULT_OPTION_COLUMNS_IS_VISIBLE) => {
    const visibilityMapping = getVisibilityMappingForMarket(marketName);

    if (visibilityMapping) {
      return columnDefsVisibility[field] && visibilityMapping[field];
    }

    return columnDefsVisibility[field];
  };

  const visible = ALL_OPTION_FIELDS.filter(getColumnVisibility).flatMap(f => [
    getOptionColumnId(OptionTypeEnum.Call, f),
    getOptionColumnId(OptionTypeEnum.Put, f),
  ]);

  const hidden = ALL_OPTION_FIELDS.filter(field => !getColumnVisibility(field)).flatMap(f => [
    getOptionColumnId(OptionTypeEnum.Call, f),
    getOptionColumnId(OptionTypeEnum.Put, f),
  ]);

  return {
    visible,
    hidden,
  };
}

export function getVisibilityMappingForMarket(marketName: string | undefined): ColumnVisibilityMapping | undefined {
  if (marketName === CME_MARKET_NAME) {
    return CME_AVAILABLE_OPTION_COLUMNS;
  }
  if (marketName === FALCONX_MARKET_NAME) {
    return FALCONX_AVAILABLE_OPTION_COLUMNS;
  }
  if (marketName === STS_DIGITAL_MARKET_NAME) {
    return STS_AVAILABLE_OPTION_COLUMNS;
  }
  return undefined;
}

/**
 * RFQ-only markets prime an RFQ from the dedicated RFQ button rather than from a cell click.
 */
export function isRfqOnlyOptionMarket(market: string | undefined): boolean {
  return market === FALCONX_MARKET_NAME || market === STS_DIGITAL_MARKET_NAME;
}
